import { Loader2, Paperclip } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useMessage } from "@/api/hooks";
import type { AttachmentChip } from "@/api/mailApi";
import { cn, getInitials } from "@/lib/utils";
import { avatarColorClass } from "./avatarColor";
import { EmailBodyFrame } from "./EmailBodyFrame";
import { REMOTE_CONTENT } from "./ReadingPane";
import { canQuickView } from "./quickView";

/**
 * The message beside an attachment in the quick view (Outlook's preview side
 * pane): subject, sender, every attachment — click one to switch the preview —
 * and the body. Remote content stays blocked here; "Open message" shows it in
 * the reading pane with the usual trust controls.
 */
export function QuickViewMessagePanel({
    messageId,
    currentAttachmentId,
    onPick,
    onOpenMessage
}: {
    messageId: string;
    currentAttachmentId?: string;
    /** Switch the preview: the message's previewable files and the one to show. */
    onPick: (files: AttachmentChip[], index: number) => void;
    onOpenMessage?: () => void;
}) {
    const detail = useMessage(messageId);
    const m = detail.data;

    if (detail.isPending) {
        return (
            <div className="flex h-full items-center justify-center text-sm text-[var(--mail-text-2)]">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> Loading message…
            </div>
        );
    }
    if (!m) return <p className="p-4 text-sm text-destructive">Couldn't load this message.</p>;

    const files = m.attachments.filter((a) => !a.isInline);
    const viewable: AttachmentChip[] = files
        .filter((a) => canQuickView(a.fileName))
        .map((a) => ({ id: a.id, name: a.fileName, contentType: a.contentType, sizeBytes: a.sizeBytes }));
    const when = new Date(m.receivedAt).toLocaleString(undefined, {
        weekday: "short", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
    });

    return (
        <div className="flex flex-col gap-3 p-3">
            <div className="rounded-lg border border-[var(--mail-line)] bg-card px-3.5 py-3">
                <h2 className="text-[15px] font-semibold leading-snug [text-wrap:balance]">{m.subject || "(no subject)"}</h2>
                {onOpenMessage ? (
                    <button
                        type="button"
                        onClick={onOpenMessage}
                        className="mt-1.5 rounded text-xs font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                        Open message
                    </button>
                ) : null}
            </div>

            <article className="rounded-lg border border-[var(--mail-line)] bg-card" aria-label="Message">
                <div className="flex items-start gap-3 px-3.5 pt-3">
                    <Avatar className="h-9 w-9 shrink-0">
                        <AvatarFallback className={cn("text-xs font-semibold", avatarColorClass(m.from.email))}>
                            {getInitials(m.from.name || m.from.email)}
                        </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-sm" title={m.from.email}>
                            <span className="font-semibold">{m.from.name || m.from.email}</span>
                            {m.from.name ? <span className="text-[13px] text-[var(--mail-text-2)]"> &lt;{m.from.email}&gt;</span> : null}
                        </p>
                        <p className="truncate text-[13px] text-[var(--mail-text-2)]">
                            To: {m.to.length === 0 ? "Undisclosed recipients" : m.to.map((r) => r.name || r.email).join("; ")}
                        </p>
                        <p className="text-xs tabular-nums text-[var(--mail-text-2)]">{when}</p>
                    </div>
                </div>

                {files.length > 0 ? (
                    <div className="flex flex-col gap-1.5 px-3.5 pt-3" role="list" aria-label="Attachments">
                        {files.map((a) => {
                            const can = canQuickView(a.fileName);
                            const current = a.id === currentAttachmentId;
                            return (
                                <button
                                    key={a.id}
                                    type="button"
                                    role="listitem"
                                    disabled={!can}
                                    aria-current={current ? "true" : undefined}
                                    title={can ? `Preview ${a.fileName}` : `${a.fileName} — no preview for this type`}
                                    onClick={() => can && onPick(viewable, viewable.findIndex((v) => v.id === a.id))}
                                    className={cn(
                                        "flex items-center gap-2 rounded border px-2.5 py-1.5 text-left text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                        current
                                            ? "border-primary bg-[var(--mail-primary-soft)]"
                                            : "border-[var(--mail-line-strong)] hover:bg-[var(--mail-hover)]",
                                        !can && "cursor-default opacity-60 hover:bg-transparent"
                                    )}
                                >
                                    <Paperclip className="h-3.5 w-3.5 shrink-0 text-[var(--mail-text-2)]" aria-hidden />
                                    <span className="min-w-0 flex-1 truncate">{a.fileName}</span>
                                    <span className="shrink-0 text-[11px] tabular-nums text-[var(--mail-text-3)]">{formatSize(a.sizeBytes)}</span>
                                </button>
                            );
                        })}
                    </div>
                ) : null}

                <div className="px-3.5 pb-4 pt-3">
                    {/* Emails are authored for a white page — keep it white in dark mode. */}
                    <div className="overflow-hidden rounded-md dark:bg-white dark:p-3">
                        <EmailBodyFrame html={m.bodyHtml} blockRemote={REMOTE_CONTENT.test(m.bodyHtml)} />
                    </div>
                </div>
            </article>
        </div>
    );
}

function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
